<svelte:options runes={false} />

<script>
  import { INSTANCE_MODES } from '../../api/runtime-config';
  import CurrencySelect from './CurrencySelect.svelte';
  import OperationsSummaryPanel from './OperationsSummaryPanel.svelte';
  import OperatorInvoiceList from './OperatorInvoiceList.svelte';
  import OperatorLoginForm from './OperatorLoginForm.svelte';
  import Pagination from './Pagination.svelte';
  import SectionHeading from './SectionHeading.svelte';

  export let brandEyebrow;
  export let isOperator;
  export let currentUser;
  export let operationsSummary;
  export let instanceMode;
  export let projects;
  export let selectedProjectId;
  export let selectedProject;
  export let canManageSettings;
  export let showArchived;
  export let selectedInvoiceId;
  export let visibleInvoices;
  export let newInvoiceCurrency;
  export let creatingInvoice;
  export let invoicePage;
  export let hasNextInvoicePage;
  export let loginEmail;
  export let loginPassword;
  export let onSelectProject;
  export let onSetArchiveView;
  export let onSelectInvoice;
  export let onCreateInvoice;
  export let onPreviousPage;
  export let onNextPage;
  export let onOpenAccountSettings;
  export let onCreateProject;
  export let onOpenProjectSettings;
  export let onLogout;
  export let onLogin;
</script>

<!-- This column is the queue, so it is named for what it holds. It used to be
     headed "Operator / Access" — a filing label for a column that is mostly a
     list of work — with "signed in" beside it, which `.section-heading > strong`
     rendered at the same size and colour as a money total. Signing in and out is
     account business and now sits with Settings; the eyebrow carries the operator's
     own name instead, as it already does on every branded route. -->
<aside class="panel operator-access-panel">
  <SectionHeading eyebrow={brandEyebrow} title={isOperator ? 'Your invoices' : 'Operator access'} />

  {#if isOperator}
    <OperationsSummaryPanel summary={operationsSummary} />
    <!-- A solo operator with one project has nothing to choose between, so the
         one project is stated rather than offered as a select of one. The guard
         is on the count, not the mode: create a second project from the adjacent
         action and this becomes the switcher on its own. -->
    <div class="project-picker">
      {#if instanceMode === INSTANCE_MODES.SOLO && projects.length === 1}
        <p class="quiet project-name">Project: <strong>{projects[0].name}</strong></p>
      {:else}
        <label>
          Project
          <select
            value={selectedProjectId}
            disabled={projects.length === 0}
            on:change={(event) => onSelectProject(event.target.value)}
          >
            {#each projects as project (project._id)}
              <option value={project._id}>{project.name}</option>
            {/each}
          </select>
        </label>
      {/if}
      {#if canManageSettings}
        <button
          class="project-add"
          type="button"
          aria-label="Create new project"
          title="Create new project"
          on:click={onCreateProject}
        >+</button>
      {/if}
    </div>
    <!-- Two buttons that were only ever distinguishable by which one was filled.
         `aria-pressed` says which view is on without relying on the fill. -->
    <div class="segmented" role="group" aria-label="Which invoices to list">
      <button
        aria-pressed={!showArchived}
        class:selected={!showArchived}
        type="button"
        on:click={() => onSetArchiveView(false)}
      >Active</button>
      <button
        aria-pressed={showArchived}
        class:selected={showArchived}
        type="button"
        on:click={() => onSetArchiveView(true)}
      >History</button>
    </div>
    <OperatorInvoiceList
      invoices={visibleInvoices}
      activeInvoiceId={selectedInvoiceId}
      onSelect={onSelectInvoice}
    />
    <!-- Paging belongs to the list it pages. The new-invoice form used to sit
         between the two, so Previous/Next read as though they walked drafts. -->
    <Pagination
      page={invoicePage}
      hasNext={hasNextInvoicePage}
      onPrevious={onPreviousPage}
      onNext={onNextPage}
    />

    <div class="new-invoice">
      <CurrencySelect
        label="New invoice currency"
        bind:value={newInvoiceCurrency}
        disabled={creatingInvoice}
      />
      <button class="primary" disabled={!selectedProject || creatingInvoice} type="button" on:click={onCreateInvoice}>
        {creatingInvoice ? 'Creating draft…' : 'New invoice'}
      </button>
    </div>

    <!-- Account business, not queue business. In the heading these two wrapped
         into a stacked pair beside a two-line eyebrow and took the top-right
         corner — the most prominent spot on the desk — for the two controls an
         operator presses least. -->
    <div class="account-row">
      {#if canManageSettings}
        <button type="button" on:click={onOpenAccountSettings}>Account settings</button>
        <button type="button" disabled={!selectedProject} on:click={onOpenProjectSettings}>Project settings</button>
      {/if}
      <button type="button" on:click={onLogout}>Sign out</button>
    </div>
  {:else if currentUser}
    <p class="quiet">This signed-in account has no operator membership.</p>
    <button type="button" on:click={onLogout}>Sign out</button>
  {:else}
    <OperatorLoginForm bind:loginEmail bind:loginPassword onLogin={onLogin} />
  {/if}
</aside>
